import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateJobGroupBody, UpdateJobGroupBody } from '@/types'
import {
	createJobGroup,
	deleteJobGroup,
	getJobGroups,
	updateJobGroup,
} from '../services/job-group'
import { commitmentMatrixKeys } from './commitment-matrix-queries'

// ── Query keys ────────────────────────────────────────────────────────────────

export const jobGroupKeys = {
	all: ['job-groups'] as const,
}

// ── GET /job-groups ───────────────────────────────────────────────────────────

export function useJobGroups() {
	return useQuery({
		queryKey: jobGroupKeys.all,
		queryFn: () => getJobGroups(),
		staleTime: 10 * 60 * 1000,
		select: (data) => data.items,
	})
}

// ── POST /job-groups ──────────────────────────────────────────────────────────

export function useCreateJobGroup() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (body: CreateJobGroupBody) => createJobGroup(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: jobGroupKeys.all })
			queryClient.invalidateQueries({ queryKey: commitmentMatrixKeys.all })
		},
	})
}

// ── PATCH /job-groups/:id ─────────────────────────────────────────────────────

export function useUpdateJobGroup() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: UpdateJobGroupBody }) =>
			updateJobGroup(id, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: jobGroupKeys.all })
			queryClient.invalidateQueries({ queryKey: commitmentMatrixKeys.all })
		},
	})
}

// ── DELETE /job-groups/:id ────────────────────────────────────────────────────

export function useDeleteJobGroup() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => deleteJobGroup(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: jobGroupKeys.all })
			queryClient.invalidateQueries({ queryKey: commitmentMatrixKeys.all })
		},
	})
}
